import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/sections/Footer';
import WhatsAppLink from '../components/global/WhatsAppLink';
import EmailLink from '../components/global/EmailLink';
import TrackedLink from '../components/global/TrackedLink';
import { CTA_IDS, CTA_LOCATIONS } from '../lib/analytics.constants';

const WaButton = ({ text, msg, ctaId, size = 'default' }) => (
  <WhatsAppLink
    ctaId={ctaId}
    ctaLocation={CTA_LOCATIONS.PAGE_CTA}
    message={msg}
    className={`inline-flex items-center gap-2 bg-[#11b36d] text-white rounded-full font-semibold hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] transition-all duration-200 ${
        size === 'large' ? 'px-8 py-4 text-base' : 'px-6 py-3 text-sm'
      }`}
  >
    <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.552 4.104 1.513 5.834L0 24l6.304-1.485A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.852 0-3.59-.5-5.093-1.373l-.365-.217-3.743.881.898-3.655-.238-.375A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
    {text}
  </WhatsAppLink>
);

const COMPARACION = [
  ['Bot genérico que responde lo mismo siempre', 'Asistente conectado a tu información real, con reglas de derivación'],
  ['Herramienta instalada y olvidada', 'Integrado a WhatsApp, CRM y sistemas que ya usás'],
  ['Promete automatizar todo desde el primer día', 'Prioriza lo que tiene impacto real — comunica los límites'],
  ['Entregan y desaparecen', 'Acompañamiento continuo con indicadores y ajustes'],
];

const OUTCOMES = [
  {
    title: 'Tu soporte responde en minutos, no en horas',
    desc: 'Sin sumar personal. Asistente conectado a tu base de conocimiento real, que escala automáticamente a una persona cuando hace falta.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: 'Ningún lead se va sin seguimiento',
    desc: 'CRM actualizado automáticamente, resúmenes de conversaciones y alertas cuando una oportunidad lleva días sin atención.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    title: 'Contratos y documentos procesados en segundos',
    desc: 'PDFs, presupuestos y pólizas extraídos y organizados automáticamente. Información lista para reportar, sin carga manual ni errores de tipeo.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
];

const PASOS = [
  {
    num: '01',
    title: 'Diagnóstico',
    desc: 'Analizamos cómo opera tu empresa hoy. Resultado: roadmap priorizado con quick wins reales y riesgo controlado.',
  },
  {
    num: '02',
    title: 'Implementación',
    desc: 'Construimos e integramos. No bots — sistemas con lógica de negocio, conectados a tus herramientas reales.',
  },
  {
    num: '03',
    title: 'Evolución',
    desc: 'La IA mejora con el tiempo. Acompañamos el cambio con métricas, ajustes y nuevas automatizaciones.',
  },
];

const POR_QUE = [
  { title: 'Software propio', desc: 'Integramos IA a tus sistemas existentes sin depender de herramientas genéricas.' },
  { title: 'Contexto local', desc: 'Conocemos cómo operan las empresas paraguayas y cómo se toman las decisiones reales.' },
  { title: 'Integración completa', desc: 'WhatsApp, correo, CRM, ERP, formularios. La IA valiosa es la que está en tu flujo real.' },
  { title: 'Sin intermediarios', desc: 'Equipo en Paraguay. Soporte real, en horario local, en español.' },
];

export default function IAPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Inteligencia Artificial para Empresas en Paraguay · IA Aplicada a Procesos Reales | eien</title>
        <meta name="description" content="Implementación de inteligencia artificial para empresas en Paraguay. Asistentes conectados a tus procesos reales, automatización de documentos e integración con WhatsApp y CRM. eien · Asunción." />
        <link rel="canonical" href="https://eientech.io/ia" />
      </Helmet>

      <div className="bg-gray-50 dark:bg-[#0a0a0a] min-h-screen">

        {/* ─── HERO ─── */}
        <section className="relative pt-24 pb-16 px-4 md:px-6 overflow-hidden">
          {/* Green glow */}
          <div
            className="absolute inset-x-0 top-0 h-[500px] pointer-events-none opacity-0 dark:opacity-100"
            style={{ background: 'radial-gradient(ellipse 80% 60% at 50% -5%, rgba(17,179,109,0.15), transparent 70%)' }}
          />
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="flex justify-center">
              <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Inteligencia Artificial</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-tight mb-6 text-[#0b2b40] dark:text-white">
              La IA que sí funciona{' '}
              <span className="text-gradient text-short">en tu empresa</span>
            </h1>
            <p className="text-xl text-slate-500 dark:text-slate-400 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
              Sin bots genéricos. Sin promesas vacías.<br />
              IA integrada a tus procesos reales, con resultados medibles desde el primer mes.
            </p>
            {/* CTA 1 — primer pico de interés */}
            <WaButton
              text="Hablar con un especialista"
              msg="Hola, quiero explorar si la IA tiene sentido para mi empresa"
              ctaId={CTA_IDS.IA_HERO_WHATSAPP}
              size="large"
            />
          </div>

          {/* Comparación — destruye la objeción antes de que aparezca */}
          <div className="relative max-w-3xl mx-auto mt-14">
            <div className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-white/[0.06]">
                <div className="px-5 py-3 bg-gray-50 dark:bg-[#0d0d0d]">
                  <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Lo que suelen vender</p>
                </div>
                <div className="px-5 py-3 bg-gray-50 dark:bg-[#0d0d0d]">
                  <p className="text-xs font-semibold text-[#11b36d] uppercase tracking-wider">Lo que hace eien</p>
                </div>
              </div>
              {COMPARACION.map(([left, right], i) => (
                <div key={i} className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-white/[0.06] border-t border-gray-100 dark:border-white/[0.06]">
                  <div className="px-5 py-4">
                    <p className="text-sm text-slate-400 dark:text-slate-500 leading-snug">{left}</p>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-sm text-[#0b2b40] dark:text-slate-300 leading-snug font-medium">{right}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── OUTCOMES ─── */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Qué cambia de verdad</p>
              </div>
              <h2 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white">
                Con IA bien implementada, tu empresa{' '}
                <span className="text-gradient text-short">hace más con menos</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {OUTCOMES.map((o, i) => (
                <div
                  key={i}
                  className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40 rounded-2xl p-6 transition-colors duration-300"
                >
                  <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d] mb-4">
                    {o.icon}
                  </div>
                  <h3 className="font-medium text-[#0b2b40] dark:text-white mb-2 leading-snug">{o.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{o.desc}</p>
                </div>
              ))}
            </div>

            {/* CTA 2 — pico de deseo, "quiero eso" */}
            <div className="text-center">
              <WaButton
                text="Quiero esto para mi empresa"
                msg="Hola, vi los resultados que genera la IA con eien y quiero saber cómo aplica a mi empresa"
                ctaId={CTA_IDS.IA_MID_WHATSAPP}
                size="large"
              />
            </div>
          </div>
        </section>

        {/* ─── PROCESO + POR QUÉ EIEN ─── */}
        <section className="py-16 px-4 md:px-6 bg-gray-50 dark:bg-[#0a0a0a]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Proceso */}
              <div>
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Cómo avanzamos</p>
                <h2 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-8">
                  Primero entendemos.<br />
                  Luego implementamos.
                </h2>
                <div className="space-y-4">
                  {PASOS.map((p) => (
                    <div key={p.num} className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-xl p-5 flex gap-4">
                      <p className="text-[#11b36d] font-mono text-sm font-semibold shrink-0 mt-0.5">{p.num}</p>
                      <div>
                        <p className="font-medium text-[#0b2b40] dark:text-white mb-1">{p.title}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Por qué eien */}
              <div>
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Por qué eien</p>
                <h2 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-8">
                  Software propio.<br />
                  Integración real.
                </h2>
                <div className="space-y-4">
                  {POR_QUE.map((item, i) => (
                    <div key={i} className="bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-xl p-5 flex gap-4">
                      <span className="text-[#11b36d] mt-0.5 shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <div>
                        <p className="font-medium text-[#0b2b40] dark:text-white mb-0.5">{item.title}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── CTA FINAL ─── */}
        <section className="py-16 px-4 md:px-6 bg-white dark:bg-[#0d0d0d]">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-50 dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] rounded-3xl p-10 text-center">
              <div className="flex justify-center">
                <p className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Sin compromiso</p>
              </div>
              <h3 className="text-2xl md:text-3xl font-thin tracking-tight text-[#0b2b40] dark:text-white mb-3">
                ¿Tiene sentido la IA para tu empresa?
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-light mb-8">
                La primera conversación es sin compromiso. Te decimos con honestidad si el diagnóstico es el paso que necesitás — y cuánto puede costar.
              </p>
              {/* CTA 3 — momento de acción */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <WaButton
                  text="Iniciar diagnóstico"
                  msg="Hola, me interesa el diagnóstico de IA para mi empresa. ¿Cómo arrancamos?"
                  ctaId={CTA_IDS.IA_FINAL_WHATSAPP}
                  size="large"
                />
                <TrackedLink to="/agendar" ctaId={CTA_IDS.IA_AGENDAR} ctaLocation={CTA_LOCATIONS.PAGE_CTA}>
                  <button className="inline-flex items-center gap-2 border border-gray-200 dark:border-white/[0.12] text-slate-600 dark:text-slate-300 px-8 py-4 rounded-full font-semibold text-base hover:border-[#11b36d]/50 hover:text-[#11b36d] transition-colors duration-200">
                    Agendar reunión
                    <span aria-hidden="true">→</span>
                  </button>
                </TrackedLink>
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-6 flex flex-wrap justify-center gap-x-2 gap-y-1">
                <EmailLink
                  email="lucas@eientech.io"
                  ctaId={CTA_IDS.IA_CLOSE_EMAIL}
                  ctaLocation={CTA_LOCATIONS.PAGE_CTA}
                  className="hover:text-[#11b36d] transition-colors"
                >
                  lucas@eientech.io
                </EmailLink>
                <span>·</span>
                <WhatsAppLink
                  ctaId={CTA_IDS.IA_CLOSE_WHATSAPP}
                  ctaLocation={CTA_LOCATIONS.PAGE_CTA}
                  message="Hola, vi la página de IA y quiero hablar con eien"
                  className="hover:text-[#11b36d] transition-colors"
                >
                  WhatsApp +595 994 459 583
                </WhatsAppLink>
                <span>·</span>
                <span>eientech.io</span>
              </p>
            </div>
          </div>
        </section>

        <section className="mt-8" id="footer">
          <Footer />
        </section>
      </div>
    </>
  );
}
